import { useRef } from 'react'
import {
  Button,
  FlexCell,
  FlexRow,
  FlexSpacer,
  LabeledInput,
  Rating,
  TextArea,
  useForm,
} from '@epam/loveship'
import { useUuiContext, UuiContexts } from '@epam/uui-core'
import css from './EditReview.module.scss'
import { showErrorNotification } from '../../../../utils/notifications/showErrorNotification'
import { showSuccessNotification } from '../../../../utils/notifications/showSuccessNotification'
import {
  LocationReviewResponse,
  PointReviewResponse,
  ReviewResponse,
} from '../../../../constants/types'
import { MAX_CHAR_LIMIT } from '../../../../constants/values'
import { usePutReviewMutation } from '../../../../store'

interface IEditReview {
  itemReview: string
  itemRating: number
}

type IEditReviewFormProps = {
  refetchReviews: (itemId: number) => void
  review: ReviewResponse
  refetchItems: (id: number) => void
  toggleEditReview: (id: number) => void
}

export const EditReviewForm = ({
  review,
  refetchReviews,
  refetchItems,
  toggleEditReview,
}: IEditReviewFormProps) => {
  const ItemIdRef = useRef(review.id)
  ItemIdRef.current = review.id
  const [putReview] = usePutReviewMutation()
  const itemId =
    (review as PointReviewResponse)?.pointId ||
    (review as LocationReviewResponse)?.locationId

  const svc = useUuiContext<UuiContexts>()
  const { lens, save, setValue } = useForm<IEditReview>({
    value: {
      itemReview: review.reviewText,
      itemRating: review.rating,
    },
    // Passed null to prevent show "Save Changes?" dialog after history.push
    // Expected behaviour with null: allways saves changes when unmount
    beforeLeave: null,
    onSave: (value) => {
      if (value.itemRating === 0) {
        return Promise.reject({
          message: 'Rating is mandatory field',
        })
      }

      return putReview({
        ...review,
        rating: value.itemRating,
        reviewText: value.itemReview,
      }).unwrap()
    },
    onSuccess: () => {
      setValue({
        itemReview: '',
        itemRating: 0,
      })
      refetchItems(itemId)
      refetchReviews(itemId)
      toggleEditReview(0)
      showSuccessNotification({ svc, message: 'Review saved' })
    },
    onError: (error) => {
      console.log('error: ', error)

      return showErrorNotification({
        svc,
        message: `Error on save: ${error.message}`,
      })
    },
    getMetadata: () => ({
      props: {
        itemRating: { isRequired: true },
        itemReview: { isRequired: false },
      },
    }),
  })

  const isMaxLimitReached =
    lens.prop('itemReview').toProps().value.length >= MAX_CHAR_LIMIT.REVIEW

  return (
    <>
      <FlexRow vPadding="12">
        <FlexCell width="auto">
          <LabeledInput
            label="Place Rating"
            {...lens.prop('itemRating').toProps()}
          >
            <Rating {...lens.prop('itemRating').toProps()} from={1} />
          </LabeledInput>
        </FlexCell>
      </FlexRow>

      <FlexRow vPadding="12">
        <FlexCell grow={1}>
          <LabeledInput
            label="Place Review"
            {...lens.prop('itemReview').toProps()}
          >
            <TextArea
              cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
              autoSize
              rows={2}
              maxLength={MAX_CHAR_LIMIT.REVIEW}
              placeholder="Place Review"
              {...lens.prop('itemReview').toProps()}
            />
          </LabeledInput>
        </FlexCell>
      </FlexRow>

      <FlexRow vPadding="12">
        <FlexSpacer />
        <Button
          color="fire"
          caption="Cancel"
          onClick={() => toggleEditReview(0)}
        />
        <Button
          caption="Save"
          onClick={() => {
            save()
          }}
          color="grass"
          cx={css.marginBtn}
        />
      </FlexRow>
    </>
  )
}
